import { Action } from "redux";

import { PostFormData } from "@/mk2/apps/forum/components/PostCreateEditForm";
import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";
import { NormalizedResponse } from "@/mk2/helpers/api";

/////////////////////////////////////////////////////////////////////
// Post Create Load

export const POST_CREATE_FETCH_TRIGGER = "POST_CREATE_FETCH_TRIGGER";
export interface PostCreateFetchTriggerAction {
    readonly type: typeof POST_CREATE_FETCH_TRIGGER;
    readonly topicSlug: string;
}
export const POST_CREATE_FETCH_REQUEST = "POST_CREATE_FETCH_REQUEST";
export interface PostCreateFetchRequestAction extends APIRequestAction {
    readonly type: typeof POST_CREATE_FETCH_REQUEST;
    readonly topicSlug: string;
}
export interface PostCreateFetchNormalizedResponse extends NormalizedResponse {
    readonly result: { topic: number };
}
export const POST_CREATE_FETCH_SUCCESS = "POST_CREATE_FETCH_SUCCESS";
export interface PostCreateFetchSuccessAction extends APISuccessAction<PostCreateFetchNormalizedResponse> {
    readonly type: typeof POST_CREATE_FETCH_SUCCESS;
    readonly topicSlug: string;
}
export const POST_CREATE_FETCH_FAILURE = "POST_CREATE_FETCH_FAILURE";
export interface PostCreateFetchFailureAction extends APIFailureAction {
    readonly type: typeof POST_CREATE_FETCH_FAILURE;
    readonly topicSlug: string;
}

export const postCreateFetchTrigger = (topicSlug: string): PostCreateFetchTriggerAction => ({
    type: POST_CREATE_FETCH_TRIGGER,
    topicSlug,
});
export const postCreateFetchApi = {
    request: (topicSlug: string): PostCreateFetchRequestAction => ({
        type: POST_CREATE_FETCH_REQUEST,
        topicSlug,
    }),
    success: (
        topicSlug: string,
        response: PostCreateFetchNormalizedResponse,
    ): PostCreateFetchSuccessAction => ({
        type: POST_CREATE_FETCH_SUCCESS,
        topicSlug,
        response,
    }),
    failure: (topicSlug: string, error: string): PostCreateFetchFailureAction => ({
        type: POST_CREATE_FETCH_FAILURE,
        topicSlug,
        error,
    }),
};

// ///////////////////////////////////////////////////////////////////
// Post Create - Progress broadcast

export const POST_CREATE_TRIGGER = "POST_CREATE_TRIGGER";
export interface PostCreateTriggerAction {
    readonly type: typeof POST_CREATE_TRIGGER;
    readonly topicSlug: string;
    readonly anonymousMode: boolean;
    readonly postFd: PostFormData;
}
export const POST_CREATE_REQUEST = "POST_CREATE_REQUEST";
export interface PostCreateRequestAction extends APIRequestAction {
    readonly type: typeof POST_CREATE_REQUEST;
    readonly topicSlug: string;
    readonly postFd: PostFormData;
}
export interface PostCreateNormalizedResponse extends NormalizedResponse {
    readonly result: { post: number; notificationsOn: number[] };
}
export const POST_CREATE_SUCCESS = "POST_CREATE_SUCCESS";
export interface PostCreateSuccessAction extends APISuccessAction<PostCreateNormalizedResponse> {
    readonly type: typeof POST_CREATE_SUCCESS;
    readonly topicSlug: string;
    readonly postFd: PostFormData;
    readonly notificationsOn: number[];
}
export const POST_CREATE_FAILURE = "POST_CREATE_FAILURE";
export interface PostCreateFailureAction extends APIFailureAction {
    readonly type: typeof POST_CREATE_FAILURE;
    readonly topicSlug: string;
    readonly postFd: PostFormData;
}

export const postCreateTrigger = (
    topicSlug: string,
    anonymousMode: boolean,
    postFd: PostFormData,
): PostCreateTriggerAction => ({
    type: POST_CREATE_TRIGGER,
    topicSlug,
    anonymousMode,
    postFd,
});
export const postCreateApi = {
    request: (topicSlug: string, postFd: PostFormData): PostCreateRequestAction => ({
        type: POST_CREATE_REQUEST,
        topicSlug,
        postFd,
    }),
    success: (
        topicSlug: string,
        postFd: PostFormData,
        notificationsOn: number[],
        response: PostCreateNormalizedResponse,
    ): PostCreateSuccessAction => ({
        type: POST_CREATE_SUCCESS,
        topicSlug,
        postFd,
        notificationsOn,
        response,
    }),
    failure: (topicSlug: string, postFd: PostFormData, error: string): PostCreateFailureAction => ({
        type: POST_CREATE_FAILURE,
        topicSlug,
        postFd,
        error,
    }),
};

// ///////////////////////////////////////////////////////////////////
// Post Create - Pending action (unauthenticated submit)

export const POST_CREATE_PENDING_ACTION_TRIGGER = "POST_CREATE_PENDING_ACTION_TRIGGER";

export interface PostCreatePendingActionTriggerAction extends Action {
    readonly type: typeof POST_CREATE_PENDING_ACTION_TRIGGER;
    readonly topicSlug: string;
    readonly postFd: PostFormData;
}

export const postCreatePendingActionTrigger = (
    topicSlug: string,
    postFd: PostFormData,
): PostCreatePendingActionTriggerAction => ({
    type: POST_CREATE_PENDING_ACTION_TRIGGER,
    topicSlug,
    postFd,
});

// ///////////////////////////////////////////////////////////////////
// Post Create - Set/unset wants MK to found discussion

export const POST_CREATE_SET_WANTS_MK_TO_FOUND_DISCUSSION =
    "POST_CREATE_SET_WANTS_MK_TO_FOUND_DISCUSSION";

export interface PostCreateSetWantsMkToFoundDiscussionAction extends Action {
    readonly type: typeof POST_CREATE_SET_WANTS_MK_TO_FOUND_DISCUSSION;
    readonly topicSlug: string;
    readonly enabled: boolean;
}

export const postCreateSetWantsMkToFoundDiscussion = (
    topicSlug: string,
    enabled: boolean,
): PostCreateSetWantsMkToFoundDiscussionAction => ({
    type: POST_CREATE_SET_WANTS_MK_TO_FOUND_DISCUSSION,
    topicSlug,
    enabled,
});

// ///////////////////////////////////////////////////////////////////
// Post Create - Set/unset anonymous mode

export const POST_CREATE_SET_ANONYMOUS_MODE = "POST_CREATE_SET_ANONYMOUS_MODE";

export interface PostCreateSetAnonymousModeAction extends Action {
    readonly type: typeof POST_CREATE_SET_ANONYMOUS_MODE;
    readonly topicSlug: string;
    readonly enabled: boolean;
}

export const postCreateSetAnonymousMode = (
    topicSlug: string,
    enabled: boolean,
): PostCreateSetAnonymousModeAction => ({
    type: POST_CREATE_SET_ANONYMOUS_MODE,
    topicSlug,
    enabled,
});
